// src/pages/JournalPage.jsx — The Philosopher's Journal
// Every "write it down" in the modules finally has somewhere to land.
// Entries live on this device only (localStorage `philosophyJournal`);
// Sofia never reads them. Exercise cards deep-link here with a prompt
// seed via router state.
//
// Entries are markdown under the hood — the toolbar inserts the markers,
// the preview and entry list render them via the shared renderer — so
// notes look composed while staying searchable, portable plain text.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Feather, Trash2, Download, Check, Pencil, X, Search,
  Bold, Italic, Heading, List, Quote, Eye, PenLine,
} from 'lucide-react';
import { showToast } from '../components/ui/toast';
import { renderMarkdown } from '../components/companion/renderMarkdown.jsx';

const STORAGE_KEY = 'philosophyJournal';

const loadEntries = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(raw?.entries) ? raw.entries : [];
  } catch {
    return [];
  }
};

const saveEntries = (entries) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }));
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });

// Wrap the current selection (or insert placeholder text) in a textarea.
const applyWrap = (textarea, before, after, placeholder) => {
  const { selectionStart: s, selectionEnd: e, value } = textarea;
  const selected = value.slice(s, e) || placeholder;
  const next = value.slice(0, s) + before + selected + after + value.slice(e);
  const cursor = s + before.length + selected.length + after.length;
  return { next, cursor };
};

// Prefix each selected line (heading, list, quote).
const applyLinePrefix = (textarea, prefix, placeholder) => {
  const { selectionStart: s, selectionEnd: e, value } = textarea;
  const lineStart = value.lastIndexOf('\n', s - 1) + 1;
  const segment = value.slice(lineStart, e) || placeholder;
  const prefixed = segment.split('\n').map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n');
  return {
    next: value.slice(0, lineStart) + prefixed + value.slice(Math.max(e, lineStart)),
    cursor: lineStart + prefixed.length,
  };
};

const TOOLBAR = [
  { label: 'Bold', icon: Bold, run: (ta) => applyWrap(ta, '**', '**', 'bold text') },
  { label: 'Italic', icon: Italic, run: (ta) => applyWrap(ta, '*', '*', 'emphasised text') },
  { label: 'Heading', icon: Heading, run: (ta) => applyLinePrefix(ta, '## ', 'Heading') },
  { label: 'List', icon: List, run: (ta) => applyLinePrefix(ta, '- ', 'list item') },
  { label: 'Quote', icon: Quote, run: (ta) => applyLinePrefix(ta, '> ', 'a line worth keeping') },
];

const JournalPage = () => {
  const location = useLocation();
  const seededPrompt = location.state?.prompt || '';

  const [entries, setEntries] = useState(loadEntries);
  const [title, setTitle] = useState('');
  const [draft, setDraft] = useState('');
  const [prompt, setPrompt] = useState(seededPrompt);
  const [previewing, setPreviewing] = useState(false);
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const composerRef = useRef(null);

  useEffect(() => { saveEntries(entries); }, [entries]);

  useEffect(() => {
    if (seededPrompt) composerRef.current?.focus();
  }, [seededPrompt]);

  const runTool = (tool) => {
    const ta = composerRef.current;
    if (!ta) return;
    const { next, cursor } = tool.run(ta);
    setDraft(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const addEntry = () => {
    if (!draft.trim()) return;
    const entry = {
      id: `j-${Date.now()}-${entries.length}`,
      createdAt: new Date().toISOString(),
      title: title.trim() || null,
      prompt: prompt || null,
      text: draft.trim(),
    };
    setEntries([entry, ...entries]);
    setDraft('');
    setTitle('');
    setPrompt('');
    setPreviewing(false);
    showToast('Entry kept', { tone: 'success' });
  };

  const removeEntry = (id) => {
    if (!window.confirm('Delete this entry? It cannot be recovered.')) return;
    setEntries(entries.filter(e => e.id !== id));
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditText(entry.text);
    setEditTitle(entry.title || '');
  };

  const commitEdit = () => {
    setEntries(entries.map(e => (e.id === editingId
      ? { ...e, text: editText.trim() || e.text, title: editTitle.trim() || null }
      : e)));
    setEditingId(null);
  };

  const exportJournal = () => {
    const lines = ['# My Philosopher\'s Journal (Profound Philosophy)', ''];
    [...entries].reverse().forEach(e => {
      lines.push(`## ${e.title ? `${e.title}: ` : ''}${formatDate(e.createdAt)}`);
      if (e.prompt) lines.push(`*Prompt: ${e.prompt}*`);
      lines.push('', e.text, '');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'philosophers-journal.md';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Search: every space-separated term must appear in title, prompt or body.
  const visibleEntries = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return entries;
    return entries.filter(e => {
      const haystack = `${e.title || ''}\n${e.prompt || ''}\n${e.text}`.toLowerCase();
      return terms.every(t => haystack.includes(t));
    });
  }, [entries, query]);

  const wordCount = useMemo(
    () => entries.reduce((sum, e) => sum + e.text.split(/\s+/).filter(Boolean).length, 0),
    [entries]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-28 pb-16">
      <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
        {/* Hero. Emptying dusk backdrop, one ember low: the day settling into words */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl overflow-hidden py-8 px-5 sm:px-6 mb-8"
        >
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src="/images/cloud-journal.webp"
              alt=""
              className="w-full h-full object-cover object-bottom opacity-60"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gunmetal/55 via-gunmetal/30 to-gunmetal" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-2">
              <Feather className="w-8 h-8 text-bright-gold" />
              <h1 className="text-3xl sm:text-4xl font-bold text-white">The Philosopher's Journal</h1>
            </div>
            <p className="text-slate-300 mb-1 max-w-xl">
              Philosophers have long kept notebooks; Marcus Aurelius' <em>Meditations</em> began
              as private notes to himself. When a module says "write it down", this is where it lands.
            </p>
            <p className="text-xs text-slate-400">
              Kept on this device only. Sofia never reads your journal.
              {entries.length > 0 && ` · ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} · ${wordCount.toLocaleString('en-AU')} words`}
            </p>
          </div>
        </motion.div>

        {/* Composer */}
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5 mb-10">
          {prompt && (
            <div className="flex items-start justify-between gap-2 mb-3 px-3 py-2 rounded-lg bg-bright-gold/10 border border-bright-gold/30">
              <p className="text-sm text-bright-gold">{prompt}</p>
              <button
                onClick={() => setPrompt('')}
                aria-label="Remove prompt"
                className="text-slate-400 hover:text-white flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-white placeholder-slate-500 px-4 py-2.5 mb-3 font-secondary text-lg focus:outline-none focus:border-bright-gold/60"
          />

          {/* Formatting toolbar + write/preview toggle */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1" role="toolbar" aria-label="Formatting">
              {TOOLBAR.map(tool => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.label}
                    onClick={() => runTool(tool)}
                    disabled={previewing}
                    aria-label={tool.label}
                    title={tool.label}
                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 disabled:opacity-30 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPreviewing(!previewing)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition-colors"
            >
              {previewing ? <PenLine className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {previewing ? 'Write' : 'Preview'}
            </button>
          </div>

          {previewing ? (
            <div className="min-h-[7rem] rounded-lg bg-slate-900/40 border border-slate-700 p-4 text-slate-200 leading-relaxed">
              {draft.trim()
                ? renderMarkdown(draft)
                : <p className="text-slate-400">Nothing to preview yet.</p>}
            </div>
          ) : (
            <textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What are you thinking?"
              rows={5}
              className="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-white placeholder-slate-500 p-4 resize-y focus:outline-none focus:border-bright-gold/60"
            />
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <span className="text-xs text-slate-400">{formatDate(new Date().toISOString())}</span>
            <button
              onClick={addEntry}
              disabled={!draft.trim()}
              className="px-5 py-2 rounded-lg bg-bright-gold/90 hover:bg-bright-gold text-gunmetal font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Keep this entry
            </button>
          </div>
        </div>

        {/* Search */}
        {entries.length > 0 && (
          <div className="relative mb-6">
            <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your journal…"
              aria-label="Search journal entries"
              className="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-white placeholder-slate-500 pl-10 pr-4 py-2.5 focus:outline-none focus:border-bright-gold/60"
            />
            {query && (
              <p role="status" className="text-xs text-slate-400 mt-2">
                {visibleEntries.length === 0
                  ? 'No entries match. Try fewer or different words.'
                  : `Showing ${visibleEntries.length} of ${entries.length} entries.`}
              </p>
            )}
          </div>
        )}

        {/* Entries */}
        {entries.length === 0 ? (
          <p className="text-slate-400 text-center py-8">
            Nothing here yet. Your first entry is one honest sentence away.
          </p>
        ) : (
          <div className="space-y-4">
            {visibleEntries.map(entry => (
              <div key={entry.id} className="rounded-xl border border-slate-700 bg-slate-800/40 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                    {formatDate(entry.createdAt)}
                  </span>
                  <div className="flex items-center gap-1">
                    {editingId === entry.id ? (
                      <button onClick={commitEdit} aria-label="Save changes" className="p-1.5 text-emerald-400 hover:text-emerald-300">
                        <Check className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => startEdit(entry)} aria-label="Edit entry" className="p-1.5 text-slate-400 hover:text-white">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => removeEntry(entry.id)} aria-label="Delete entry" className="p-1.5 text-slate-400 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {entry.prompt && (
                  <p className="text-xs text-bright-gold/80 mb-2">Prompt: {entry.prompt}</p>
                )}
                {editingId === entry.id ? (
                  <>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Title (optional)"
                      className="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-white placeholder-slate-500 px-3 py-2 mb-2 font-secondary focus:outline-none focus:border-bright-gold/60"
                    />
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={5}
                      className="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-white p-3 resize-y focus:outline-none focus:border-bright-gold/60"
                    />
                  </>
                ) : (
                  <>
                    {entry.title && (
                      <h3 className="text-xl font-bold text-white mb-2">{entry.title}</h3>
                    )}
                    <div className="text-slate-200 leading-relaxed">
                      {renderMarkdown(entry.text)}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {entries.length > 0 && (
          <button
            onClick={exportJournal}
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            Export as text
          </button>
        )}
      </div>
    </div>
  );
};

export default JournalPage;
